import React from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { useAuth } from '@/contexts/AuthContext';
import { AlertCircle, Building2, ArrowRight, LogOut, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

/**
 * Guard component that checks if the current tenant is suspended.
 * If suspended, shows a message and allows switching to an active tenant.
 * Platform admins bypass this guard and can access suspended tenants.
 */
export const SuspendedTenantGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentTenant, tenants, switchTenant, loading, isPlatformAdmin } = useTenant();
  const { logout, authenticated, loading: authLoading } = useAuth();

  // Show loading while auth or tenant context is loading
  if (authLoading || loading) {
    return <>{children}</>;
  }

  // If not authenticated, pass through
  if (!authenticated) {
    return <>{children}</>;
  }

  // Platform admins can access suspended tenants
  if (isPlatformAdmin) {
    return <>{children}</>;
  }

  // Check if current tenant is suspended
  const isSuspended = currentTenant?.status === 'SUSPENDED';

  if (!isSuspended) {
    return <>{children}</>;
  }

  // Find active tenants the user can switch to
  const activeTenants = tenants.filter(t => t.status === 'ACTIVE');

  const handleSwitchTenant = async (tenantId: string) => {
    try {
      await switchTenant(tenantId);
      // After switching, the component will re-render and show the main app
    } catch (error) {
      console.error('Failed to switch tenant:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="max-w-2xl w-full">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <div>
              <CardTitle className="text-2xl">Organization Suspended</CardTitle>
              <CardDescription className="mt-1">
                {currentTenant?.tenantName}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
            <p className="text-sm text-muted-foreground">
              Your organization <span className="font-semibold text-foreground">{currentTenant?.tenantName}</span> has been suspended.
              Please contact <span className="font-semibold">Enveu Flow Support</span> for assistance.
            </p>
          </div>

          {activeTenants.length > 0 && (
            <div className="space-y-4">
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium mb-3">Switch to Another Organization</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  You have access to {activeTenants.length} other active organization{activeTenants.length !== 1 ? 's' : ''}:
                </p>
                <div className="space-y-2">
                  {activeTenants.map((tenant) => (
                    <div
                      key={tenant.tenantId}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">{tenant.tenantName}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                              Active
                            </Badge>
                            <span className="text-xs text-muted-foreground capitalize">{tenant.role?.toLowerCase()}</span>
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleSwitchTenant(tenant.tenantId)}
                        size="sm"
                        variant="outline"
                      >
                        Switch
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="border-t pt-4">
            <p className="text-sm text-muted-foreground mb-4">
              For support, please contact: <a href="mailto:support@enveu.com" className="text-primary hover:underline">support@enveu.com</a>
            </p>
            <Button
              onClick={logout}
              variant="outline"
              className="w-full"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
